'use client'

import React, { useEffect, useState } from 'react'
import { useRouter, usePathname } from 'next/navigation'
import { useUserStore } from '@/stores/user-store-provider'
import { Button, notification } from 'antd'
import { CartListAction } from '@/actions/shop'
import { AddCartAction } from '@/actions/shop'
import { loginToRedirectUrl } from '@/utils/index'
import { getCommonText } from '@/utils/i18n-helpers'
import { BookInfoAction } from '@/actions/shop'
import ErrorMessage from '@/components/ErrorMessage'

export default function AddCartButton({ children, lng, bookName, bookId, type = 'circle', isDisabled = false }: { children?: React.ReactNode; lng: string; bookName: string; bookId: number | number[]; type?: string; isDisabled?: boolean }) {
  const router = useRouter()
  const curPath = usePathname()
  const addCartText = getCommonText(lng, 'addToCart')
  const isPurchasedText = getCommonText(lng, 'isPurchased')
  const addToCartSuccessText = getCommonText(lng, 'addToCartSuccess')
  const { isLogin, bookIdList, cartNum, updateCartNum } = useUserStore((state) => state)
  const [api, contextHolder] = notification.useNotification({ top: 90, getContainer: () => document.body, maxCount: 1 })
  const [success, setSuccess] = useState(false)
  const [isHasBuy, setIsHasBuy] = useState(false)
  const [btnDisabled, setBtnDisabled] = useState(isDisabled)
  const [isLoading, setIsLoading] = useState(false)

  const addCart = async () => {
    if (!isLogin) {
      loginToRedirectUrl(router, curPath)
      return
    }
    const { success: resSuccess } = await AddCartAction({ list: Array.isArray(bookId) ? bookId : [bookId] })
    if (resSuccess) {
      const allBooksPurchased = Array.isArray(bookId) ? bookId.every((id) => bookIdList.includes(id)) : bookIdList.includes(bookId)
      if (allBooksPurchased) {
        setIsHasBuy(true)
      } else {
        setIsHasBuy(false)
        setSuccess(true)
        const newItemsCount = Array.isArray(bookId) ? bookId.length : 1
        const optimisticCartNum = cartNum + newItemsCount
        updateCartNum(optimisticCartNum)

        const { result: cartListData, success } = await CartListAction({})
        if (success) {
          updateCartNum(cartListData.notBuyList.length)
        }
      }
    }
  }

  useEffect(() => {
    const checkAddCartStatus = async () => {
      if (isLogin && !btnDisabled && type === 'round-static') {
        setIsLoading(true)
        const { result: _bookInfoData, success, errorLangs } = await BookInfoAction({ bookId })
        if (!success) return <ErrorMessage error={errorLangs[lng]} />
        setBtnDisabled(!!_bookInfoData.hasAddCart)
        setIsLoading(false)
      }
    }
    checkAddCartStatus()
  }, [isLogin])

  useEffect(() => {
    if (success) {
      api.open({
        message: '',
        description: (
          <p className="pr-[30px] leading-[20px]">
            {`${bookName} `}
            {!isHasBuy && <span className="text-red">{addToCartSuccessText}</span>}
            {isHasBuy && <span className="text-red">{isPurchasedText}</span>}
          </p>
        ),
        className: 'endao-notice',
        duration: 3,
        style: {
          padding: '4px 0 12px 20px',
          width: 256,
          fontSize: 13,
          borderRadius: 5,
          border: '1px solid #ffc6cc'
        }
      })
      setSuccess(false)
    }
  }, [success])

  return (
    <>
      {contextHolder}
      {type === 'round' && (
        <Button shape="round" danger disabled={isDisabled} onClick={addCart} title={addCartText}>
          {children}
        </Button>
      )}
      {type === 'round-static' && (
        <Button shape="round" danger disabled={btnDisabled} loading={isLoading} onClick={addCart} title={addCartText}>
          {children}
        </Button>
      )}
      {type === 'circle' && (
        <span title={addCartText} onClick={addCart} className="group flex h-[32px] w-[32px] cursor-pointer items-center justify-center rounded-[50%] border border-red transition-all duration-[400ms] hover:!bg-red">
          <svg xmlns="http://www.w3.org/2000/svg" fill="currentColor" width="19.83" height="18.67" viewBox="0 0 19.833332061767578 18.66666603088379" className="text-red transition-all duration-[400ms] group-hover:!text-white">
            <path
              d="M3.41650390625 5.64013671875C3.41650390625 5.64013671875 3.41650390625 5.64013671875 3.41650390625 5.64013671875L16.41650390625 5.64013671875C16.41650390625 5.64013671875 16.41650390625 5.64013671875 16.41650390625 5.64013671875L16.41650390625 15.64013671875C16.41650390625 16.744706218411586 15.521073405911586 17.64013671875 14.41650390625 17.64013671875L5.41650390625 17.64013671875C4.3119344065884135 17.64013671875 3.41650390625 16.744706218411586 3.41650390625 15.64013671875Z"
              fillOpacity="0"
              strokeOpacity="1"
              stroke="currentColor"
              fill="currentColor"
              strokeWidth="1"
            />
            <path
              d="M6.41650390625 3.64013671875C6.41650390625 2.5355672190884126 7.3119344065884135 1.64013671875 8.41650390625 1.64013671875L11.41650390625 1.64013671875C12.521073405911586 1.64013671875 13.41650390625 2.5355672190884135 13.41650390625 3.64013671875L13.41650390625 5.64013671875C13.41650390625 5.64013671875 13.41650390625 5.64013671875 13.41650390625 5.64013671875L6.41650390625 5.64013671875C6.41650390625 5.64013671875 6.41650390625 5.64013671875 6.41650390625 5.64013671875Z"
              fillOpacity="0"
              strokeOpacity="1"
              stroke="currentColor"
              fill="currentColor"
              strokeWidth="1"
            />
          </svg>
        </span>
      )}
    </>
  )
}
